import "./datetimepicker.scss";
import * as React from "react";
import { type RefObject } from "react";
import { type Moment } from "moment";
import { formatDate, formatDateTime } from "../../Utilities/Generic/Strings";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import Tether from "../Tether/Tether";
import Calendar, { type CalendarChangeValue, type CalendarProps } from "./Calendar";

type ChangeCallbackType = null | ((value: boolean) => void);

interface Props extends Omit<
    CalendarProps,
    "defaultValue" |
    "matchesMedia"
> {
    renderTarget?: (
        targetRef: React.RefObject<HTMLElement>,
        onChangeShow: (open: boolean) => void,
        changeCallback: (callback: ChangeCallbackType) => void
    ) => React.ReactNode;
    onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
    onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
    placeholder?: string;
    hideEndTime?: boolean;
    readOnly?: boolean;
    disabled?: boolean;
    closeOnSelect?: boolean;
    className?: string;
}

type State = {
    open: boolean;
    start: Moment | null;
    end: Moment | null;
};

export default class DateTimePicker extends React.Component<Props, State> {
    public changeCallback: ChangeCallbackType = null;

    private ref: React.RefObject<any> = React.createRef();

    constructor(props: Readonly<Props>) {
        super(props);

        this.state = {
            open : false,
            start: props.start ? props.start : null,
            end  : props.end ? props.end : null,
        };
    }

    public componentDidMount(): void {
        document.body.addEventListener("click", this.handleBodyClick);
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(!this.props.start && prevProps.start) {
            this.setState({
                start: null,
            });
        } else if(
            this.props.start &&
            prevProps.start !== this.props.start &&
            this.props.start !== this.state.start
        ) {
            this.setState({
                start: this.props.start,
            });
        }

        if(!this.props.end && prevProps.end) {
            this.setState({
                end: null,
            });
        } else if(
            this.props.end &&
            prevProps.end !== this.props.end &&
            this.props.end !== this.state.end
        ) {
            this.setState({
                end: this.props.end,
            });
        }
    }

    public componentWillUnmount(): void {
        document.body.removeEventListener("click", this.handleBodyClick);
    }

    public handleBodyClick = (e: MouseEvent): void => {
        if(!this.state.open) {
            return;
        }

        if(this.ref.current) {
            if(this.ref.current.contains(e.target)) {
                return;
            }
        }

        if(!(e.target instanceof Element)) {
            return;
        }

        const elements = document.querySelectorAll(".tether-element");

        for(let i = 0, l = elements.length; i < l; i++) {
            if(elements[i].contains(e.target)) {
                return;
            }
        }

        this.setState({
            open: false,
        });
    };

    public handleChange = (value: CalendarChangeValue, name: string): void => {
        if(this.props.disabled) {
            return;
        }

        if(typeof this.props.onChange === "function") {
            this.props.onChange(value, name);
        }

        const { start, end } = value;

        this.setState(prevState => {
            let open = true;

            if(this.props.closeOnSelect && start) {
                if(this.props.range) {
                    if(end) {
                        // Check to see if the day is the same, just the time changed
                        if(!prevState.end || !end.isSame(prevState.end, "day")) {
                            open = parseInt(end.format("YYYY"), 10).toString(10).length < 4;
                        }
                    }
                } else {
                    // Check to see if the day is the same, just the time changed
                    if(!prevState.start || !start.isSame(prevState.start, "day")) {
                        open = parseInt(start.format("YYYY"), 10).toString(10).length < 4;
                    }
                }
            }

            return {
                open,
                start,
                end,
            };
        });
    };

    public handleClear = (): void => {
        if(typeof this.props.onClear === "function") {
            this.props.onClear(this.props.name);
        }

        this.setState({
            start: null,
            end  : null,
        });
    };

    public handleFocus = (e: React.FocusEvent<HTMLDivElement>): void => {
        if(this.props.disabled) {
            e.preventDefault();
            e.target.blur();

            return;
        }

        if(typeof this.props.onFocus === "function") {
            this.props.onFocus(e);
        }
    };

    public handleBlur = (e: React.FocusEvent<HTMLDivElement>): void => {
        if(this.props.disabled) {
            e.preventDefault();

            return;
        }

        if(typeof this.props.onBlur === "function") {
            this.props.onBlur(e);
        }
    };

    public handleChangeShow = (open: boolean): void => {
        this.setState({
            open,
        });
    };

    public handleDone = (): void => {
        this.setState({
            open: false,
        });

        if(typeof this.props.onDone === "function") {
            this.props.onDone();
        }

        if(typeof this.changeCallback === "function") {
            this.changeCallback(false);
        }
    };

    public toggleOpen = (): void => {
        if(this.props.readOnly || this.props.disabled) {
            return;
        }

        this.setState(prevState => ({
            open: !prevState.open,
        }));
    };

    public formatDateRange = (): string => {
        const { start, end } = this.state;

        if(start === null) {
            return "";
        }

        let results;

        const startAtStart = start.hour() === 0 && start.minute() === 0 && start.second() === 0;
        const startAtEnd = start.hour() === 23 && start.minute() === 59 && start.second() === 59;

        if((!this.props.hideEndTime && startAtStart) || !this.props.time || (this.props.hideEndTime && startAtEnd)) {
            results = formatDate(start);
        } else {
            results = formatDateTime(start);
        }

        if(end && this.props.range) {
            const endAtEnd = end.hour() === 23 && end.minute() === 59 && end.second() === 59;

            if(endAtEnd || !this.props.time) {
                results += ` - ${formatDate(end)}`;
            } else {
                results += ` - ${formatDateTime(end)}`;
            }
        }

        return results;
    };

    public prepareChange = (callback: ChangeCallbackType): void => {
        this.changeCallback = callback;
    };

    public renderTarget = (targetRef: React.RefObject<HTMLDivElement>): React.ReactNode => {
        if(typeof this.props.renderTarget === "function") {
            return this.props.renderTarget(targetRef, this.handleChangeShow, this.prepareChange);
        }

        const classes = [ "date-time-text" ];
        let rangeText = this.formatDateRange();
        const hasValue = rangeText !== "";

        if(!hasValue) {
            classes.push("placeholder");
            rangeText = typeof this.props.placeholder === "undefined" ? (
                this.props.range ? "Select Date Range..." : "Select Date..."
            ) : this.props.placeholder;
        }

        const inputClasses = [ "date-time-value", "input" ];

        if(this.props.disabled) {
            inputClasses.push("disabled");
        }

        const clearable = typeof this.props.onClear !== "undefined" && hasValue;

        if(clearable) {
            inputClasses.push("clearable");
        }

        return (
            <div
                ref={targetRef}
                className={inputClasses.join(" ")}
                tabIndex={0}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                onClick={this.toggleOpen}
            >
                <div className={classes.join(" ")}>{rangeText}</div>
                {!this.props.readOnly && (
                    <div className="date-time-indicator">
                        <FontAwesomeLight icon={this.state.open ? "caret-up" : "caret-down"} />
                    </div>
                )}
                {clearable && (
                    <div className="date-time-clear" onClick={this.handleClear}>
                        <FontAwesomeLight icon="times" />
                    </div>
                )}
            </div>
        );
    };

    public render(): React.ReactNode {
        const {
            renderTarget,
            onChange,
            onClear,
            onBlur,
            onFocus,
            onDone,
            placeholder,
            hideEndTime,
            disabled,
            readOnly,
            closeOnSelect,
            ...props
        } = this.props;

        const open = this.state.open && !this.props.readOnly && !this.props.disabled;

        return (
            <div
                ref={this.ref}
                className="date-time-picker"
            >
                <Tether
                    target={this.renderTarget}
                    element={(popupRef: RefObject<HTMLDivElement>): React.ReactNode => (open && (
                        <div
                            ref={popupRef}
                            className="calendar-popup"
                        >
                            <Calendar
                                {...props}
                                onChange={this.handleChange}
                                onClear={this.handleClear}
                                onDone={this.handleDone}
                                defaultValue={[ this.state.start, this.state.end ]}
                            />
                        </div>
                    ))}
                />
            </div>
        );
    }
}
